import { __extends, __assign } from 'tslib';
import { useLocalization } from '@shared-ui/localization-context';
import * as React from 'react';
import { classNames, liveAnnounce, Level, isElementVisible } from 'uitk-react-utils';
import styles from './uitk-date-picker.css';
import { DatePickerModel } from './dependencies/date-picker-model.esm.js';
import { UitkDateField } from './uitk-date-field.esm.js';

var getClassNameList = classNames.bind(styles);
var UitkDatePickerTriggeredSelector;
(function (UitkDatePickerTriggeredSelector) {
    UitkDatePickerTriggeredSelector["START"] = "start";
    UitkDatePickerTriggeredSelector["END"] = "end";
    UitkDatePickerTriggeredSelector["NONE"] = "none";
})(UitkDatePickerTriggeredSelector || (UitkDatePickerTriggeredSelector = {}));
var UitkDatePickerRenderType;
(function (UitkDatePickerRenderType) {
    UitkDatePickerRenderType["DIALOG"] = "dialog";
    UitkDatePickerRenderType["MENU"] = "menu";
})(UitkDatePickerRenderType || (UitkDatePickerRenderType = {}));
var DatePicker = /** @class */ (function (_super) {
    __extends(DatePicker, _super);
    function DatePicker(props) {
        var _this = _super.call(this, props) || this;
        var initialValues = _this.setInitialDates();
        var selection = new DatePickerModel(initialValues.lastValidDate, props.singleDateSelect);
        var name;
        if (props.triggeredSelector) {
            name = props.triggeredSelector;
        }
        else {
            name = props.dateFieldProps.showDatePicker ? UitkDatePickerTriggeredSelector.START : '';
        }
        _this.startDomRef = props.startDateRef ? props.startDateRef : React.createRef();
        _this.endDomRef = props.endDateRef ? props.endDateRef : React.createRef();
        _this.state = {
            localizedText: {
                checkInInstructions: function () { return props.l10n.formatText('datePicker.fromDateInstructions.a11yLabel'); },
                checkOutInstructions: function () { return props.l10n.formatText('datePicker.toDateInstructions.a11yLabel'); },
                dateFieldSelected: function () { return props.l10n.formatText('datePicker.selectedDateField.allyLabel'); },
                doneHeader: function () { return props.l10n.formatText('datePicker.doneHeader'); },
                clearHeader: function () { return props.l10n.formatText('datePicker.clearHeader'); },
                selectEndDate: function () { return props.l10n.formatText('datePicker.dateInput.endDate.placeholder'); },
                selectStartDate: function () { return props.l10n.formatText('datePicker.dateInput.startDate.placeholder'); },
                clearedDates: function () { return props.l10n.formatText('datepicker.cleared.dates'); }
            },
            name: name,
            selection: selection
        };
        _this.getDatePickerProps = _this.getDatePickerProps.bind(_this);
        _this.onSubmit = _this.onSubmit.bind(_this);
        _this.onClearDates = _this.onClearDates.bind(_this);
        _this.onDismiss = _this.onDismiss.bind(_this);
        _this.onSelectStartMode = _this.onSelectStartMode.bind(_this);
        _this.onSelectEndMode = _this.onSelectEndMode.bind(_this);
        _this.openDatePicker = _this.openDatePicker.bind(_this);
        _this.selectionClassNames = _this.selectionClassNames.bind(_this);
        _this.setInitialDates = _this.setInitialDates.bind(_this);
        _this.updateDatePicker = _this.updateDatePicker.bind(_this);
        _this.updateViewport = _this.updateViewport.bind(_this);
        return _this;
    }
    DatePicker.prototype.UNSAFE_componentWillMount = function () {
        this.updateStateWithProps(this.props);
    };
    DatePicker.prototype.componentDidMount = function () {
        var props = this.props;
        if (props.onMountCB) {
            props.onMountCB(this.startDomRef, this.endDomRef);
        }
    };
    DatePicker.prototype.UNSAFE_componentWillReceiveProps = function (nextProps) {
        var _a = this.props, startDate = _a.startDate, endDate = _a.endDate, validStartDate = _a.validStartDate;
        if (nextProps.startDate !== startDate || nextProps.endDate !== endDate) {
            // PRESELECT DATES
            this.preselectedStart = nextProps.startDate;
            this.preselectedEnd = nextProps.endDate;
        }
        if (nextProps.validStartDate !== validStartDate) {
            this.setFirstValidDate(nextProps);
        }
        this.updateStateWithProps(nextProps);
    };
    DatePicker.prototype.componentDidUpdate = function (prevProps) {
        var _a = this.props, dateFieldProps = _a.dateFieldProps, daysBookableInAdvance = _a.daysBookableInAdvance;
        var selection = this.state.selection;
        if (prevProps.daysBookableInAdvance !== daysBookableInAdvance) {
            this.lastValidDate = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() + daysBookableInAdvance, this.today.getDate());
            /* This is not the proper way to set state, but given the architecture
               that the selection state property has, is not possible to use setState
               in the selection property. If we try to use setState to update the property
               we will need to use spread operator for keeping the previous values when updating,
               and doing that makes the selection lose the reference for the class DatePickerSelectionState.
               This leads to errors when trying to access to the public methods of that class.
              */
            selection.lastValidEndDate = this.lastValidDate;
        }
        if (!prevProps.dateFieldProps.showDatePicker && dateFieldProps.showDatePicker) {
            if (this.isOpenInternallyDriven) {
                // Reset flag
                this.isOpenInternallyDriven = undefined;
            }
            else {
                // Client driven show date picker
                this.openDatePicker(UitkDatePickerTriggeredSelector.START, false);
            }
        }
    };
    DatePicker.prototype.onSubmit = function () {
        var _a = this.props, l10n = _a.l10n, onSubmitCB = _a.onSubmitCB;
        var _b = this.state, name = _b.name, selection = _b.selection;
        var start = selection.start, end = selection.end;
        this.resetLiveAnnounce();
        this.updateDatePicker();
        // PRESELECT DATES
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.preselectedEnd = l10n.formatDate(end, {
            dateFormat: 'iso-date',
            raw: 'yyyy-MM-dd'
        });
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        this.preselectedStart = l10n.formatDate(start, {
            dateFormat: 'iso-date',
            raw: 'yyyy-MM-dd'
        });
        this.focusOnTrigger(name);
        // Submits and closes the datepicker
        if (onSubmitCB) {
            onSubmitCB(start, end, this.isDialog);
        }
    };
    DatePicker.prototype.onClearDates = function () {
        var onClearDatesCB = this.props.onClearDatesCB;
        this.clearDatePicker();
        if (onClearDatesCB) {
            onClearDatesCB();
        }
    };
    DatePicker.prototype.onDismiss = function (noUpdate) {
        var onDismissCB = this.props.onDismissCB;
        var _a = this.state, name = _a.name, selection = _a.selection;
        selection.start = null;
        selection.end = null;
        this.updateDatePicker();
        this.resetLiveAnnounce();
        this.focusOnTrigger(name);
        if (onDismissCB) {
            onDismissCB(this.isDialog, noUpdate);
        }
    };
    DatePicker.prototype.onSelectStartMode = function (noUpdate) {
        var onSelectStartModeCB = this.props.onSelectStartModeCB;
        var _a = this.state, localizedText = _a.localizedText, selection = _a.selection;
        selection.setCurrentTrigger('start');
        liveAnnounce(localizedText.checkInInstructions(), Level.POLITE);
        onSelectStartModeCB && onSelectStartModeCB();
        if (!noUpdate) {
            this.updateDatePicker();
        }
    };
    DatePicker.prototype.onSelectEndMode = function (noUpdate) {
        var onSelectEndModeCB = this.props.onSelectEndModeCB;
        var _a = this.state, localizedText = _a.localizedText, selection = _a.selection;
        selection.setCurrentTrigger('end');
        liveAnnounce(localizedText.checkOutInstructions(), Level.POLITE);
        onSelectEndModeCB && onSelectEndModeCB();
        if (!noUpdate) {
            this.updateDatePicker();
        }
    };
    DatePicker.prototype.getDatePickerProps = function () {
        var _a = this.props, analytics = _a.analytics, buttonText = _a.buttonText, clearButtonText = _a.clearButtonText, dateFieldProps = _a.dateFieldProps, datePickerContent = _a.datePickerContent, doubleMonths = _a.doubleMonths, endSelectionPlaceholder = _a.endSelectionPlaceholder, firstDayOfWeek = _a.firstDayOfWeek, footerText = _a.footerText, holidays = _a.holidays, l10n = _a.l10n, onDoneButtonClickCB = _a.onDoneButtonClickCB, renderType = _a.renderType, singleMode = _a.singleMode, startSelectionPlaceholder = _a.startSelectionPlaceholder, submitDisabled = _a.submitDisabled;
        var _b = this.state, localizedText = _b.localizedText, selection = _b.selection;
        var startSelection = selection.start;
        var endSelection = selection.end;
        var placeholderStartSelection = startSelectionPlaceholder || localizedText.selectStartDate();
        var placeholderEndSelection = endSelectionPlaceholder || localizedText.selectEndDate();
        var formattedStartSelection = startSelection
            ? l10n.formatDate(startSelection, { dateFormat: 'full', skeleton: 'MMMEd' })
            : placeholderStartSelection;
        var formattedEndSelection = endSelection
            ? l10n.formatDate(endSelection, { dateFormat: 'full', skeleton: 'MMMEd' })
            : placeholderEndSelection;
        var datePickerAnalytics = analytics || {};
        var closeAnalytics = datePickerAnalytics.closeAnalytics, doneAnalytics = datePickerAnalytics.doneAnalytics, nextPageAnalytics = datePickerAnalytics.nextPageAnalytics, previousPageAnalytics = datePickerAnalytics.previousPageAnalytics;
        var doneButtonText = buttonText || localizedText.doneHeader();
        var clearDatesButtonText = clearButtonText || localizedText.clearHeader();
        var startSelectedMsg = this.isSelected(true) ? localizedText.dateFieldSelected() : '';
        var endSelectedMsg = this.isSelected(false) ? localizedText.dateFieldSelected() : '';
        return {
            buttonText: doneButtonText,
            clearButtonText: clearDatesButtonText,
            closeAnalytics: closeAnalytics,
            datePickerContent: datePickerContent,
            doneAnalytics: doneAnalytics,
            doubleMonths: doubleMonths,
            endSelectedMsg: endSelectedMsg,
            firstDayOfWeek: firstDayOfWeek,
            firstValidDate: this.firstValidDate,
            footerText: footerText,
            formattedEndSelection: formattedEndSelection,
            formattedStartSelection: formattedStartSelection,
            holidays: holidays,
            labels: {
                end: dateFieldProps.endLabel,
                start: dateFieldProps.startLabel
            },
            lastValidDate: this.lastValidDate,
            nextPageAnalytics: nextPageAnalytics,
            onDoneButtonClickCB: onDoneButtonClickCB,
            onSelectEndMode: this.onSelectEndMode,
            onSelectStartMode: this.onSelectStartMode,
            previousPageAnalytics: previousPageAnalytics,
            renderType: renderType,
            selection: selection,
            selectionClassNames: this.selectionClassNames,
            singleMode: singleMode,
            startSelectedMsg: startSelectedMsg,
            submitDisabled: submitDisabled,
            today: this.today,
            updateDatePicker: this.updateDatePicker
        };
    };
    DatePicker.prototype.setFirstValidDate = function (props) {
        this.firstValidDate = this.today;
        if (props.validStartDate) {
            try {
                this.firstValidDate = props.l10n.parseLocalDateString(props.validStartDate, {
                    dateFormat: 'iso',
                    raw: 'yyyy-MM-dd'
                });
            }
            catch (error) {
                console.error(error);
            }
            if (this.firstValidDate < this.today && !props.allowPastDateSearch) {
                this.firstValidDate = this.today;
            }
        }
    };
    DatePicker.prototype.setInitialDates = function () {
        var _a = this.props, cutoffDate = _a.cutoffDate, daysBookableInAdvance = _a.daysBookableInAdvance, endDate = _a.endDate, l10n = _a.l10n, midnightBookingOffset = _a.midnightBookingOffset, singleMode = _a.singleMode, startDate = _a.startDate;
        this.today = new Date();
        this.today.setHours(12);
        this.today.setMinutes(0);
        this.today.setSeconds(0);
        this.today.setMilliseconds(0);
        this.lastValidDate = new Date(this.today.getFullYear(), this.today.getMonth(), this.today.getDate() + daysBookableInAdvance);
        if (cutoffDate) {
            try {
                this.lastValidDate = l10n.parseLocalDateString(cutoffDate, {
                    dateFormat: 'iso',
                    raw: 'yyyy-MM-dd'
                });
            }
            catch (error) {
                console.error(error);
            }
        }
        this.setFirstValidDate(this.props);
        this.tomorrow = new Date();
        this.tomorrow.setDate(this.firstValidDate.getDate() + 1);
        // Enabled previous date for midnight to 4am (default) bookings
        // This is to allow customers to book late night so they can check-in immediately.
        if (midnightBookingOffset) {
            var now = new Date();
            if (now.getHours() < midnightBookingOffset) {
                this.today.setDate(this.today.getDate() - 1);
            }
        }
        if (endDate && !singleMode) {
            this.preselectedEnd = endDate;
        }
        if (startDate) {
            this.preselectedStart = startDate;
        }
        return {
            firstValidDate: this.firstValidDate,
            lastValidDate: this.lastValidDate
        };
    };
    DatePicker.prototype.updateViewport = function (isDialog) {
        this.isDialog = isDialog;
    };
    DatePicker.prototype.updateStateWithProps = function (props) {
        var l10n = props.l10n, singleMode = props.singleMode, triggeredSelector = props.triggeredSelector;
        var selection = this.state.selection;
        if (this.preselectedStart) {
            var start = this.firstValidDate;
            try {
                start = l10n.parseLocalDateString(this.preselectedStart, {
                    dateFormat: 'iso',
                    raw: 'yyyy-MM-dd'
                });
            }
            catch (error) {
                console.error(error);
            }
            start.setHours(12);
            if (start >= this.firstValidDate) {
                selection.start = start;
            }
            else {
                this.clearDatePicker();
                this.preselectedStart = null;
                this.preselectedEnd = null;
            }
        }
        if (this.preselectedEnd) {
            var end = this.tomorrow;
            try {
                end = l10n.parseLocalDateString(this.preselectedEnd, {
                    dateFormat: 'iso',
                    raw: 'yyyy-MM-dd'
                });
            }
            catch (error) {
                console.error(error);
            }
            end.setHours(12);
            selection.end = end;
        }
        if (!this.preselectedStart && !this.preselectedEnd) {
            selection.start = null;
            selection.end = null;
        }
        // Maybe breaking
        singleMode && delete selection.end;
        if (triggeredSelector) {
            selection.setCurrentTrigger(triggeredSelector);
        }
    };
    DatePicker.prototype.resetLiveAnnounce = function () {
        liveAnnounce('', Level.POLITE);
    };
    DatePicker.prototype.openDatePicker = function (name, isOpenInternallyDriven) {
        if (isOpenInternallyDriven === void 0) { isOpenInternallyDriven = true; }
        var props = this.props;
        this.isOpenInternallyDriven = isOpenInternallyDriven;
        if (name === 'start') {
            this.onSelectStartMode(true);
        }
        else {
            this.onSelectEndMode(true);
        }
        this.setState({ name: name });
        if (props.onOpenCB) {
            props.onOpenCB(name, this.isDialog);
        }
    };
    DatePicker.prototype.isSelected = function (isStart) {
        var selection = this.state.selection;
        return ((isStart && selection.isSelectingStart) ||
            (!isStart && selection.isSelectingEnd) ||
            (isStart && !selection.isSelectingStart && !selection.isSelectingEnd));
    };
    DatePicker.prototype.selectionClassNames = function (isStart) {
        return getClassNameList('uitk-date-picker-top-bar-selection', { selected: this.isSelected(isStart) });
    };
    DatePicker.prototype.createCustomEvent = function (value, onChangeEvent) {
        var event = new CustomEvent('change');
        var input = document.createElement('input');
        input.value = value;
        input.addEventListener('change', function (inputEvent) {
            onChangeEvent(inputEvent);
        });
        input.dispatchEvent(event);
    };
    DatePicker.prototype.updateDatePicker = function () {
        var _a = this.state, localizedText = _a.localizedText, selection = _a.selection;
        var _b = this.props, onStartDateResultSelection = _b.onStartDateResultSelection, onEndDateResultSelection = _b.onEndDateResultSelection;
        if (onStartDateResultSelection) {
            this.createCustomEvent(selection.start, onStartDateResultSelection);
        }
        if (onEndDateResultSelection) {
            this.createCustomEvent(selection.end, onEndDateResultSelection);
        }
        this.setState({
            localizedText: localizedText,
            selection: selection
        });
    };
    DatePicker.prototype.clearDatePicker = function () {
        var _a = this.state, localizedText = _a.localizedText, selection = _a.selection;
        selection.start = null;
        selection.end = null;
        liveAnnounce(localizedText.clearedDates());
        this.updateDatePicker();
    };
    DatePicker.prototype.focusOnTrigger = function (name) {
        var _a;
        var focusRef = name === 'start' ? this.startDomRef : this.endDomRef;
        if (isElementVisible(focusRef.current)) {
            (_a = focusRef === null || focusRef === void 0 ? void 0 : focusRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    };
    DatePicker.prototype.render = function () {
        // Const { props } = this;
        var _a = this.props, dialogWrapper = _a.dialogWrapper, dateFieldProps = _a.dateFieldProps, doubleMonths = _a.doubleMonths, onChangeEndDateCB = _a.onChangeEndDateCB, onChangeMonthCB = _a.onChangeMonthCB, onChangeStartDateCB = _a.onChangeStartDateCB, onScrollHandler = _a.onScrollHandler, preventAutoFocus = _a.preventAutoFocus, singleMode = _a.singleMode, triggeredSelector = _a.triggeredSelector, useClearDates = _a.useClearDates, useSingleTrigger = _a.useSingleTrigger;
        var _b = this.state, name = _b.name, selection = _b.selection;
        var dateFieldWrapperClasses = dateFieldProps.dateFieldWrapperClasses, dateFormat = dateFieldProps.dateFormat, endAnalytics = dateFieldProps.endAnalytics, endId = dateFieldProps.endId, endInvalid = dateFieldProps.endInvalid, endLabel = dateFieldProps.endLabel, endName = dateFieldProps.endName, endPlaceholder = dateFieldProps.endPlaceholder, mediumDateFormatProp = dateFieldProps.mediumDateFormatProp, onChange = dateFieldProps.onChange, useCustomTrigger = dateFieldProps.useCustomTrigger, routerLinkWrapper = dateFieldProps.routerLinkWrapper, showDatePicker = dateFieldProps.showDatePicker, space = dateFieldProps.space, stacked = dateFieldProps.stacked, startAnalytics = dateFieldProps.startAnalytics, startId = dateFieldProps.startId, startInvalid = dateFieldProps.startInvalid, startLabel = dateFieldProps.startLabel, startName = dateFieldProps.startName, startPlaceholder = dateFieldProps.startPlaceholder;
        var endProps;
        var _dialogWrapper;
        if (dialogWrapper) {
            _dialogWrapper = dialogWrapper;
        }
        else {
            _dialogWrapper = function (children) { return (React.createElement("div", { id: "date-picker", className: getClassNameList('uitk-dialog-layer', 'uitk-dialog-layer-responsive') }, children)); };
        }
        var propsForDateField = {
            dateFieldWrapperClasses: dateFieldWrapperClasses,
            dateFormat: dateFormat,
            dialogWrapper: _dialogWrapper,
            mediumDateFormatProp: mediumDateFormatProp,
            onChange: onChange,
            onChangeEndDate: onChangeEndDateCB,
            onChangeMonth: onChangeMonthCB,
            onChangeStartDate: onChangeStartDateCB,
            onClearDates: this.onClearDates,
            onDismiss: this.onDismiss,
            onScrollHandler: onScrollHandler,
            onSubmit: this.onSubmit,
            openDatePicker: this.openDatePicker,
            preventAutoFocus: preventAutoFocus,
            routerLinkWrapper: routerLinkWrapper,
            showDatePicker: showDatePicker,
            singleMode: singleMode,
            space: space,
            stacked: stacked,
            triggeredField: triggeredSelector || name,
            useCustomTrigger: useCustomTrigger,
            useClearDates: useClearDates,
            useSingleTrigger: useSingleTrigger
        };
        var datePickerProps = this.getDatePickerProps();
        var startProps = {
            analytics: startAnalytics,
            dateType: UitkDatePickerTriggeredSelector.START,
            domRef: this.startDomRef,
            doubleMonths: doubleMonths,
            id: startId,
            invalid: startInvalid,
            label: startLabel,
            name: startName || 'startDate',
            placeholder: startPlaceholder,
            value: selection.start
        };
        var allProps = __assign({ datePickerProps: datePickerProps, endProps: endProps, startProps: startProps, updateViewport: this.updateViewport }, propsForDateField);
        if (!singleMode) {
            endProps = {
                analytics: endAnalytics,
                dateType: UitkDatePickerTriggeredSelector.END,
                domRef: this.endDomRef,
                doubleMonths: doubleMonths,
                id: endId,
                invalid: endInvalid,
                label: endLabel,
                name: endName || 'endDate',
                placeholder: endPlaceholder,
                value: selection.end
            };
            allProps.endProps = endProps;
        }
        return React.createElement(UitkDateField, __assign({}, allProps));
    };
    DatePicker.defaultProps = {
        daysBookableInAdvance: 500,
        midnightBookingOffset: 4,
        preventAutoFocus: false,
        singleMode: false
    };
    return DatePicker;
}(React.Component));
var UitkDatePicker = function (props) {
    var l10n = useLocalization();
    return React.createElement(DatePicker, __assign({ l10n: l10n }, props));
};
UitkDatePicker.displayName = 'UitkDatePicker';
var UitkNewDatePicker = UitkDatePicker;

export { DatePicker, UitkDatePicker, UitkDatePickerRenderType, UitkDatePickerTriggeredSelector, UitkNewDatePicker };
